import React from "react";
import { BoltIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import {
  getStepNamesForWorkflowType,
  getStepDisplayLabel,
  isAutomatedStep,
} from "../../util/workflowSteps";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";

export type StepProgressStatus = "complete" | "current" | "upcoming";

export type HorizontalStepProgressStep = {
  id: string;
  label: string;
  status: StepProgressStatus;
  /** Show lightning bolt instead of checkmark for completed steps (e.g. consumer lookup) */
  isAutomated?: boolean;
  /** Automated steps are not clickable - no scroll target */
  isAutomatedStep?: boolean;
  stepNumber?: number;
};

type HorizontalStepProgressIndicatorProps = {
  steps: HorizontalStepProgressStep[];
  onStepClick?: (stepId: string) => void;
};

/**
 * Build HorizontalStepProgressStep[] from workflow type for Workflow Setup pages.
 * Uses the same step definitions as request processing (DataRequestProcessingContext).
 *
 * @param workflowType - The workflow type (DELETE, PROFILING_OPT_OUT, etc.)
 * @param currentStepIndex - 0-based index of the step being configured (for status: current)
 */
export function buildStepsFromWorkflowType(
  workflowType: WorkflowType,
  currentStepIndex: number,
): HorizontalStepProgressStep[] {
  const stepNames = getStepNamesForWorkflowType(workflowType);
  const manualStepIndices = stepNames
    .map((name, i) => (isAutomatedStep(name) ? -1 : i))
    .filter((i) => i >= 0);
  const manualStepNumbers = Object.fromEntries(manualStepIndices.map((idx, i) => [idx, i + 1]));

  return stepNames.map((name, index) => {
    const stepId = name.replace(/\s+/g, "-");
    const status: StepProgressStatus =
      index < currentStepIndex ? "complete" : index === currentStepIndex ? "current" : "upcoming";
    const automated = isAutomatedStep(name);
    return {
      id: stepId,
      label: getStepDisplayLabel(name),
      status,
      isAutomated: automated && status === "complete",
      isAutomatedStep: automated,
      stepNumber: manualStepNumbers[index],
    };
  });
}

const ConnectorLine = () => (
  <span className="flex shrink-0 w-[96px] flex items-center justify-center mt-[12px]">
    <svg
      width="96"
      height="4"
      viewBox="0 0 96 4"
      preserveAspectRatio="none"
      className="text-primary-600"
    >
      <line x1="0" y1="2" x2="96" y2="2" stroke="currentColor" strokeWidth="2" />
    </svg>
  </span>
);

export function HorizontalStepProgressIndicator({
  steps,
  onStepClick,
}: HorizontalStepProgressIndicatorProps) {
  return (
    <nav aria-label="Workflow steps" className="mb-8">
      {/* Single row: circle, connector, circle, connector, ... - connectors sit between circles */}
      <ol className="flex items-start justify-center">
        {steps.map((step, index) => {
          const isClickable = Boolean(onStepClick) && !step.isAutomatedStep;

          return (
            <React.Fragment key={step.id}>
              {index > 0 && <ConnectorLine />}
              <li className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick?.(step.id)}
                  className={clsx(
                    "flex flex-col items-center",
                    isClickable && "cursor-pointer hover:opacity-80",
                    !isClickable && "cursor-default",
                  )}
                  aria-current={step.status === "current" ? "step" : undefined}
                  aria-label={`${step.label}${step.status === "current" ? " (current step)" : ""}`}
                >
                  {/* Step circle - 28x28 */}
                  <span
                    className={clsx(
                      "flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full border-2 text-[12px] font-semibold",
                      step.isAutomatedStep
                        ? "border-primary-600 bg-primary-600 text-white"
                        : "border-primary-600 bg-white text-primary-600",
                    )}
                  >
                    {step.isAutomatedStep ? (
                      <BoltIcon className="h-4 w-4 text-white" />
                    ) : (
                      step.stepNumber ?? index + 1
                    )}
                  </span>

                  {/* Label below */}
                  <span
                    className={clsx(
                      "mt-1.5 text-[10px] font-medium text-center px-1 text-primary-600",
                      isClickable && "underline",
                    )}
                  >
                    {step.label}
                  </span>
                </button>
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
